import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import clientService from '../../../services/clientService';
import { ArrowLeft, Save, CreditCard, User, FolderKanban, Calendar, DollarSign, Hash } from 'lucide-react';

const PaymentForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    invoice_number: '',
    amount: '',
    payment_date: '',
    due_date: '',
    payment_method: 'bank_transfer',
    status: 'paid',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    init();
  }, [id]);

  const init = async () => {
    try {
      const c = await clientService.getClients({ per_page: 100 });
      setClients(c.data || []);
      // Prefill from query params when creating
      const qClientId = searchParams.get('client_id');
      const qProjectId = searchParams.get('project_id');
      if (qClientId && !isEditMode) {
        setFormData(prev => ({ ...prev, client_id: qClientId }));
        // fetch projects for that client
        try {
          const p = await clientService.getProjects({ per_page: 100, client_id: qClientId });
          setProjects(p.data || []);
          if (qProjectId) {
            setFormData(prev => ({ ...prev, project_id: qProjectId }));
          }
        } catch (e) { console.error(e); }
      } else {
        const p = await clientService.getProjects({ per_page: 100 });
        setProjects(p.data || []);
      }
      if (isEditMode) {
        await loadPayment();
      } else {
        const inv = await clientService.generateInvoiceNumber();
        setFormData(prev => ({ ...prev, invoice_number: inv.invoice_number, payment_date: new Date().toISOString().slice(0,10) }));
      }
    } catch(e) { console.error(e); }
  };

  // When client changes, refetch projects for that client to filter dropdown
  useEffect(() => {
    const fetchByClient = async () => {
      if (formData.client_id) {
        try {
          const p = await clientService.getProjects({ per_page: 100, client_id: formData.client_id });
          setProjects(p.data || []);
        } catch (e) { console.error(e); }
      }
    };
    fetchByClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.client_id]);

  const loadPayment = async () => {
    try {
      setLoading(true);
      const res = await clientService.getPayment(id);
      setFormData({ ...res, amount: res.amount || '', project_id: res.project_id || '' });
    } catch (e) {
      alert('Error loading payment');
      navigate('/admin/payments');
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.client_id) e.client_id = 'Client is required';
    if (!formData.invoice_number.trim()) e.invoice_number = 'Invoice number is required';
    if (!formData.amount || Number(formData.amount) <= 0) e.amount = 'Valid amount is required';
    if (!formData.payment_date) e.payment_date = 'Payment date is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      const payload = { ...formData, amount: Number(formData.amount) };
      if (isEditMode) {
        await clientService.updatePayment(id, payload);
        alert('Payment updated');
      } else {
        await clientService.createPayment(payload);
        alert('Payment created');
      }
      navigate('/admin/payments');
    } catch (e) {
      console.error(e);
      alert('Error saving payment');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="mb-8">
        <button onClick={() => navigate('/admin/payments')} className="flex items-center gap-2 text-gray-600 hover:text-primary mb-4"><ArrowLeft className="w-5 h-5"/>Back to Payments</button>
        <h1 className="text-3xl font-bold text-primary mb-2">{isEditMode ? 'Edit Payment' : 'Add New Payment'}</h1>
        <p className="text-gray-600">Link to client/project, set amount and status</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><CreditCard className="w-5 h-5"/> Payment Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Client *</label>
                  <select name="client_id" value={formData.client_id} onChange={handleChange} className={`w-full px-4 py-2 border-2 rounded-lg ${errors.client_id ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}>
                    <option value="">Select Client</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                  {errors.client_id && <p className="text-red-500 text-sm mt-1">{errors.client_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Project (optional)</label>
                  <select name="project_id" value={formData.project_id || ''} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg">
                    <option value="">No Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Invoice Number *</label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input name="invoice_number" value={formData.invoice_number} onChange={handleChange} className={`w-full pl-10 pr-4 py-2 border-2 rounded-lg ${errors.invoice_number ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`} placeholder="INV-2025-00001" />
                  </div>
                  {errors.invoice_number && <p className="text-red-500 text-sm mt-1">{errors.invoice_number}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Amount (฿) *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input type="number" name="amount" value={formData.amount} onChange={handleChange} className={`w-full pl-10 pr-4 py-2 border-2 rounded-lg ${errors.amount ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`} placeholder="50000" />
                  </div>
                  {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Payment Date *</label>
                  <input type="date" name="payment_date" value={formData.payment_date || ''} onChange={handleChange} className={`w-full px-4 py-2 border-2 rounded-lg ${errors.payment_date ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`} />
                  {errors.payment_date && <p className="text-red-500 text-sm mt-1">{errors.payment_date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Due Date</label>
                  <input type="date" name="due_date" value={formData.due_date || ''} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Payment Method</label>
                  <select name="payment_method" value={formData.payment_method} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg">
                    {['cash','bank_transfer','upi','card','cheque','other'].map(m => <option key={m} value={m}>{m.replace('_',' ')}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-primary mb-4">Notes</h2>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows="4" className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg" placeholder="Notes for this payment..." />
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">Status</h3>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg">
                {['paid','pending','overdue','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">Actions</h3>
              <button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? 'Saving...' : (<><Save className="w-5 h-5"/> {isEditMode ? 'Update Payment' : 'Create Payment'}</>)}
              </button>
              <button type="button" onClick={()=>navigate('/admin/payments')} className="w-full mt-3 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold">Cancel</button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
